import React, { useState, useRef } from "react";
import axios from "axios";
import { Button, Input, Spinner } from "../../shared/ui";
import { Camera, FileSignature, AlertCircle, CheckCircle, X, Plus } from "lucide-react";
import { uploadReceipt } from "../../lib/uploadReceipt";
import { localTodayISO } from "../../lib/localDate";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHOTO_POSITIONS = [
  { code: "front", label: "Front" },
  { code: "back", label: "Back" },
  { code: "left", label: "Left side" },
  { code: "right", label: "Right side" },
];

const SignaturePad = ({ value, onChange, label, testId }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  const getCtx = () => canvasRef.current?.getContext("2d");
  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (canvasRef.current.width / rect.width), y: y * (canvasRef.current.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const ctx = getCtx();
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = getCtx();
    const p = getPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2;
    ctx.stroke();
  };
  const end = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const ctx = getCtx();
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange(null);
  };

  return (
    <div className="space-y-1">
      <label className="text-xs font-bold uppercase text-slate-600">{label}</label>
      <div className="relative bg-white border-2 border-slate-300 rounded">
        <canvas
          ref={canvasRef}
          width={400}
          height={120}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
          data-testid={testId}
        />
        {value && (
          <span className="absolute top-1 right-1 text-[10px] bg-green-500 text-white px-2 py-0.5 rounded font-bold uppercase">Signed</span>
        )}
      </div>
      <button type="button" onClick={clear} className="text-[11px] text-slate-500 hover:text-red-600">
        Clear signature
      </button>
    </div>
  );
};

/**
 * Dry Hire Start Wizard
 *  - 4 photos minimum (front/back/left/right)
 *  - Hours + KM at handover
 *  - Cleaning + damages + conditions signatures
 *  - Client signature
 */
export const DryHireStartModal = ({ machine, clients, currentUser, onClose, onCreated }) => {
  const [form, setForm] = useState({
    client_id: "",
    client_name: "",
    client_contact: "",
    client_phone: "",
    site_address: "",
    start_date: localTodayISO(),
    start_time: (() => { const d = new Date(); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })(),
    expected_return_date: "",
    starting_hours: "",
    starting_kms: "",
    rate_type: "daily",
    rate_amount: 0,
    cleaning_condition: "Clean",
    damages_notes: "",
    conditions_notes: "",
    client_signed_by_name: "",
  });
  const [photos, setPhotos] = useState([]);
  const [sigs, setSigs] = useState({
    cleaning_condition_signature: null,
    damages_signature: null,
    conditions_signature: null,
    client_signature: null,
  });
  const [submitting, setSubmitting] = useState(false);

  const canSeeMoney = ["admin", "manager", "office_staff"].includes(currentUser?.role);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addPhoto = async (file, position) => {
    if (!file) return;
    try {
      const r = await uploadReceipt(file);
      setPhotos((p) => [...p, { url: r.url, position, file_name: r.file_name }]);
      toast.success(`Photo "${position}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't upload photo");
    }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (photos.length < 4) {
      toast.error("Need at least 4 condition photos before handing over");
      return;
    }
    for (const s of ["cleaning_condition_signature", "damages_signature", "conditions_signature"]) {
      if (!sigs[s]) {
        toast.error(`Missing signature: ${s.replace("_signature","").replace(/_/g, " ")}`);
        return;
      }
    }
    if (!form.client_name && !form.client_id) {
      toast.error("Pick or enter a client");
      return;
    }
    if (form.starting_hours === "" || form.starting_kms === "") {
      toast.error("Starting hours and KM are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/machines/${machine.id}/dry-hire/start`, {
        ...form,
        starting_hours: parseFloat(form.starting_hours),
        starting_kms: parseFloat(form.starting_kms),
        rate_amount: canSeeMoney ? parseFloat(form.rate_amount || 0) : 0,
        photos,
        ...sigs,
      });
      toast.success("Dry hire started");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start dry hire");
    } finally {
      setSubmitting(false);
    }
  };

  const requiredPositionsMissing = PHOTO_POSITIONS.filter((pos) => !photos.find((p) => p.position === pos.code));

  return (
    <div className="space-y-4 p-4" data-testid="dry-hire-start-modal">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold uppercase">Dry Hire — Start</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="dry-hire-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase font-bold text-slate-500">Client</label>
          <select value={form.client_id} onChange={(e) => {
            const c = (clients || []).find((cl) => cl.id === e.target.value);
            setForm((f) => ({ ...f, client_id: e.target.value, client_name: c?.name || "" }));
          }} className="h-9 w-full bg-white border-2 border-slate-200 rounded-lg px-2 text-sm" data-testid="dry-hire-client-select">
            <option value="">— Pick a client —</option>
            {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <Input label="Or new client name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="dry-hire-client-name" />
        <Input label="Client contact" value={form.client_contact} onChange={(e) => set("client_contact", e.target.value)} />
        <Input label="Client phone" value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} />
        <Input label="Site address" value={form.site_address} onChange={(e) => set("site_address", e.target.value)} className="md:col-span-2" />
        <Input label="Start date *" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="dry-hire-start-date" />
        <Input label="Expected return" type="date" value={form.expected_return_date} onChange={(e) => set("expected_return_date", e.target.value)} />
        <Input label="Starting hours *" type="number" step="0.1" value={form.starting_hours} onChange={(e) => set("starting_hours", e.target.value)} data-testid="dry-hire-start-hours" />
        <Input label="Starting KM *" type="number" step="0.1" value={form.starting_kms} onChange={(e) => set("starting_kms", e.target.value)} data-testid="dry-hire-start-kms" />
        {canSeeMoney && (
          <>
            <div>
              <label className="text-xs uppercase font-bold text-slate-500">Rate type</label>
              <select value={form.rate_type} onChange={(e) => set("rate_type", e.target.value)} className="h-9 w-full bg-white border-2 border-slate-200 rounded-lg px-2 text-sm">
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <Input label="Rate $" type="number" step="0.01" value={form.rate_amount} onChange={(e) => set("rate_amount", e.target.value)} />
          </>
        )}
      </div>

      {/* Photos */}
      <div className="bg-slate-50 border border-slate-200 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold uppercase text-sm flex items-center gap-2">
            <Camera className="w-4 h-4 text-orange-500" /> Condition Photos ({photos.length} / 4 minimum)
          </h4>
          {requiredPositionsMissing.length > 0 && (
            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">
              Still need: {requiredPositionsMissing.map((p) => p.label).join(", ")}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {PHOTO_POSITIONS.map((pos) => {
            const got = photos.find((p) => p.position === pos.code);
            return (
              <label key={pos.code} className={`block border-2 border-dashed rounded p-2 text-center cursor-pointer ${got ? "border-green-400 bg-green-50" : "border-slate-300 bg-white hover:border-orange-400"}`}>
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => addPhoto(e.target.files?.[0], pos.code)}
                  data-testid={`dry-hire-photo-${pos.code}`} />
                {got ? (
                  <>
                    <img src={got.url} alt={pos.label} className="w-full h-20 object-cover rounded mb-1" />
                    <div className="text-[10px] font-bold uppercase text-green-700">{pos.label} ✓</div>
                  </>
                ) : (
                  <>
                    <Plus className="w-6 h-6 mx-auto text-slate-400" />
                    <div className="text-[10px] font-bold uppercase text-slate-500">{pos.label}</div>
                  </>
                )}
              </label>
            );
          })}
        </div>
        {photos.length > 4 && (
          <div className="mt-2 text-xs text-slate-500">Extra photos: {photos.length - 4}</div>
        )}
      </div>

      {/* Notes */}
      <div className="grid md:grid-cols-3 gap-3">
        <Input label="Cleaning condition" value={form.cleaning_condition} onChange={(e) => set("cleaning_condition", e.target.value)} placeholder="Clean / Light dust / Dirty" />
        <Input label="Damages noted" value={form.damages_notes} onChange={(e) => set("damages_notes", e.target.value)} placeholder="e.g. scratch on left panel" />
        <Input label="Other conditions" value={form.conditions_notes} onChange={(e) => set("conditions_notes", e.target.value)} placeholder="Any other notes" />
      </div>

      {/* Signatures */}
      <div className="bg-slate-50 border border-slate-200 rounded p-3">
        <h4 className="font-bold uppercase text-sm mb-3 flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-orange-500" /> Sign-offs at handover
        </h4>
        <div className="grid md:grid-cols-2 gap-3">
          <SignaturePad label="Cleaning condition agreed *" value={sigs.cleaning_condition_signature} onChange={(v) => setSigs((s) => ({ ...s, cleaning_condition_signature: v }))} testId="dry-hire-sig-cleaning" />
          <SignaturePad label="Damages agreed *" value={sigs.damages_signature} onChange={(v) => setSigs((s) => ({ ...s, damages_signature: v }))} testId="dry-hire-sig-damages" />
          <SignaturePad label="Hire conditions agreed *" value={sigs.conditions_signature} onChange={(v) => setSigs((s) => ({ ...s, conditions_signature: v }))} testId="dry-hire-sig-conditions" />
          <div className="space-y-2">
            <Input label="Client signed by (name)" value={form.client_signed_by_name} onChange={(e) => set("client_signed_by_name", e.target.value)} />
            <SignaturePad label="Client signature" value={sigs.client_signature} onChange={(v) => setSigs((s) => ({ ...s, client_signature: v }))} testId="dry-hire-sig-client" />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="dry-hire-submit">
          {submitting ? "Starting..." : "Start Dry Hire"}
        </Button>
      </div>
    </div>
  );
};

export default DryHireStartModal;
